import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterRenderEffect,
  computed,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import type * as Leaflet from 'leaflet';
import { Observable, map } from 'rxjs';
import { AutocompleteComponent, AutocompleteOption } from '../autocomplete/autocomplete.component';
import { FIELD_HINT } from '../form/field-styles';
import { uniqueId } from '../form/ids';
import { IconComponent } from '../icon/icon.component';
import { SpinnerComponent } from '../feedback/spinner.component';

/** La posizione scelta: un punto, un indirizzo o entrambi (FR-003). */
export interface LocationValue {
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly addressLine: string | null;
  readonly postalCode: string | null;
  readonly municipality: string | null;
  readonly province: string | null;
  readonly country: string | null;
}

/** Un luogo suggerito dalla geocodifica (forma di `GET /api/geocoding/search`). */
export interface LocationPlace extends LocationValue {
  readonly label: string;
  readonly latitude: number;
  readonly longitude: number;
}

export type LocationSearch = (query: string) => Observable<readonly LocationPlace[]>;
export type LocationReverse = (
  latitude: number,
  longitude: number,
) => Observable<LocationPlace | null>;

/** Centro e zoom iniziali senza un punto: l'Italia intera. */
const DEFAULT_CENTER: [number, number] = [42.5, 12.5];
const DEFAULT_ZOOM = 5;
const POINT_ZOOM = 17;
/** Valore dell'opzione che rappresenta l'indirizzo già scelto. */
const CURRENT = 'current';

export const EMPTY_LOCATION: LocationValue = {
  latitude: null,
  longitude: null,
  addressLine: null,
  postalCode: null,
  municipality: null,
  province: null,
  country: null,
};

/** "Via Roma 88, 24122 Bergamo BG". */
export function formatLocationValue(value: LocationValue | null): string {
  if (!value) {
    return '';
  }
  const place = [value.postalCode, value.municipality, value.province]
    .map((part) => part?.trim())
    .filter((part) => !!part)
    .join(' ');
  return [value.addressLine?.trim(), place].filter((part) => !!part).join(', ');
}

/** Coordinate a sei decimali (circa 10 cm), come le conserva il server. */
export function formatCoordinates(latitude: number, longitude: number): string {
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

/** Indirizzo pubblico di Google Maps per un punto, senza chiavi né librerie (research R-12). */
export function mapsUrl(latitude: number, longitude: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
}

/**
 * Posizione del cantiere (FR-003, research R-12): campo indirizzo con suggerimenti (almeno 3
 * lettere), mappa Leaflet con lo sfondo configurato e un segnaposto trascinabile, indirizzo del
 * punto scelto, coordinate in monospazio e "Apri in Maps". Funziona anche senza geocodifica:
 * l'indirizzo scritto a mano resta valido. Leaflet si carica solo quando c'è lo sfondo della
 * mappa (`tileUrl`), quindi non entra nel bundle iniziale né nei test senza mappa.
 */
@Component({
  selector: 'bs-location-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AutocompleteComponent, IconComponent, SpinnerComponent],
  host: { class: 'flex flex-col gap-2' },
  template: `
    <bs-autocomplete
      [label]="label()"
      placeholder="Via, numero civico e comune"
      [required]="required()"
      [error]="error()"
      [minChars]="3"
      [allowFreeText]="true"
      [search]="suggest"
      [selected]="currentOption()"
      [testId]="testId() ? testId() + '-address' : null"
      (selectedChange)="choose($event)"
      (freeText)="typed($event)"
    />
    <div
      #mapElement
      role="region"
      [attr.aria-label]="'Mappa: ' + hint"
      [attr.aria-describedby]="hintId"
      class="relative z-0 h-56 overflow-hidden rounded-2xl border border-stone-200 bg-stone-100 dark:border-stone-800 dark:bg-stone-800"
      [attr.data-testid]="testId() ? testId() + '-map' : null"
    >
      @if (!tileUrl()) {
        <p
          class="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-stone-600 dark:text-stone-300"
        >
          Mappa non disponibile: indica l'indirizzo nel campo qui sopra.
        </p>
      } @else if (mapStatus() === 'loading') {
        <div class="absolute inset-0 flex items-center justify-center">
          <bs-spinner label="Caricamento della mappa…" />
        </div>
      } @else if (mapStatus() === 'error') {
        <p
          class="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-stone-600 dark:text-stone-300"
        >
          Non riusciamo a caricare la mappa: indica l'indirizzo nel campo qui sopra.
        </p>
      }
    </div>
    <p [id]="hintId" [class]="hintClass">{{ hint }}</p>
    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" aria-live="polite">
      @if (point(); as current) {
        <span
          class="font-mono text-stone-600 dark:text-stone-300"
          [attr.data-testid]="testId() ? testId() + '-coordinates' : null"
        >
          {{ current.text }}
        </span>
        <a
          class="inline-flex min-h-11 items-center gap-1.5 font-semibold text-ink underline underline-offset-2 dark:text-stone-50"
          target="_blank"
          rel="noopener noreferrer"
          [href]="current.maps"
          [attr.data-testid]="testId() ? testId() + '-maps' : null"
        >
          <bs-icon name="map-pin" [size]="16" />
          Apri in Maps
          <span class="sr-only">(si apre in una nuova scheda)</span>
        </a>
      }
      @if (resolving()) {
        <span class="text-stone-600 dark:text-stone-300">Ricerca dell'indirizzo del punto…</span>
      } @else if (notFound()) {
        <span
          class="text-stone-600 dark:text-stone-300"
          [attr.data-testid]="testId() ? testId() + '-not-found' : null"
        >
          Nessun indirizzo trovato per questo punto: se serve, scrivilo a mano.
        </span>
      }
    </div>
  `,
})
export class LocationPickerComponent {
  private readonly document = inject(DOCUMENT);

  readonly label = input('Posizione');
  readonly required = input(false);
  readonly error = input<string | null>(null);
  readonly testId = input<string | null>(null);
  /** Suggerimenti di indirizzo; un elenco vuoto lascia scrivere l'indirizzo a mano. */
  readonly search = input.required<LocationSearch>();
  /** Indirizzo del punto scelto sulla mappa. */
  readonly reverse = input<LocationReverse | null>(null);
  /** Sfondo della mappa (`{z}/{x}/{y}`); senza, la mappa non compare. */
  readonly tileUrl = input<string | null>(null);
  readonly attribution = input('');
  /** La posizione: si legge e si scrive (`[(value)]`). */
  readonly value = model<LocationValue | null>(null);

  protected readonly hintId = uniqueId('bs-location-picker');
  protected readonly hintClass = FIELD_HINT;
  protected readonly hint =
    "Cerca l'indirizzo oppure tocca la mappa per indicare il punto; trascina il segnaposto per correggerlo.";

  protected readonly mapStatus = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
  protected readonly resolving = signal(false);
  protected readonly notFound = signal(false);

  protected readonly currentOption = computed<AutocompleteOption | null>(() => {
    const text = formatLocationValue(this.value());
    return text ? { value: CURRENT, label: text } : null;
  });
  protected readonly point = computed(() => {
    const value = this.value();
    if (value?.latitude == null || value.longitude == null) {
      return null;
    }
    return {
      text: formatCoordinates(value.latitude, value.longitude),
      maps: mapsUrl(value.latitude, value.longitude),
    };
  });

  private readonly mapElement = viewChild.required<ElementRef<HTMLElement>>('mapElement');
  private places: readonly LocationPlace[] = [];
  private leaflet: typeof Leaflet | null = null;
  private map: Leaflet.Map | null = null;
  private marker: Leaflet.Marker | null = null;
  private reverseToken = 0;

  /** La ricerca per `bs-autocomplete`: i luoghi diventano opzioni. */
  protected readonly suggest = (query: string): Observable<AutocompleteOption[]> =>
    this.search()(query).pipe(
      map((places) => {
        this.places = places;
        return places.map((place, index) => ({ value: String(index), label: place.label }));
      }),
    );

  constructor() {
    const destroyRef = inject(DestroyRef);

    // La mappa nasce quando c'è lo sfondo; poi il segnaposto segue la posizione.
    afterRenderEffect(() => {
      const url = this.tileUrl();
      const element = this.mapElement().nativeElement;
      if (url && !this.map && this.mapStatus() === 'idle') {
        void this.createMap(element, url, this.attribution());
      }
    });
    afterRenderEffect(() => {
      const value = this.value();
      if (this.mapStatus() === 'ready') {
        this.showPoint(value);
      }
    });

    destroyRef.onDestroy(() => {
      this.reverseToken++;
      this.map?.remove();
      this.map = null;
    });
  }

  protected choose(option: AutocompleteOption | null): void {
    if (!option || option.value === CURRENT) {
      return;
    }
    const place = this.places[Number(option.value)];
    if (!place) {
      return;
    }
    this.notFound.set(false);
    this.value.set({
      latitude: place.latitude,
      longitude: place.longitude,
      addressLine: place.addressLine,
      postalCode: place.postalCode,
      municipality: place.municipality,
      province: place.province,
      country: place.country,
    });
    this.map?.setView([place.latitude, place.longitude], POINT_ZOOM);
  }

  /** Indirizzo scritto a mano: resta com'è, il punto (se c'è) non cambia. */
  protected typed(text: string): void {
    if (text === formatLocationValue(this.value())) {
      return;
    }
    const current = this.value() ?? EMPTY_LOCATION;
    this.value.set({
      ...current,
      addressLine: text,
      postalCode: null,
      municipality: null,
      province: null,
    });
  }

  /** Il punto è cambiato sulla mappa: coordinate nuove e, se si trova, l'indirizzo del punto. */
  protected moved(latitude: number, longitude: number): void {
    const lat = round(latitude);
    const lng = round(longitude);
    const current = this.value() ?? EMPTY_LOCATION;
    this.value.set({ ...current, latitude: lat, longitude: lng });
    const reverse = this.reverse();
    this.notFound.set(false);
    if (!reverse) {
      return;
    }
    const token = ++this.reverseToken;
    this.resolving.set(true);
    reverse(lat, lng).subscribe({
      next: (place) => {
        if (token !== this.reverseToken) {
          return;
        }
        this.resolving.set(false);
        if (!place) {
          this.notFound.set(true);
          return;
        }
        this.value.set({
          latitude: lat,
          longitude: lng,
          addressLine: place.addressLine,
          postalCode: place.postalCode,
          municipality: place.municipality,
          province: place.province,
          country: place.country,
        });
      },
      error: () => {
        if (token === this.reverseToken) {
          this.resolving.set(false);
          this.notFound.set(true);
        }
      },
    });
  }

  private async createMap(
    element: HTMLElement,
    tileUrl: string,
    attribution: string,
  ): Promise<void> {
    this.mapStatus.set('loading');
    try {
      const module = (await import('leaflet')) as typeof Leaflet & { default?: typeof Leaflet };
      const L = module.default ?? module;
      this.leaflet = L;
      const value = this.value();
      const start: [number, number] | null =
        value?.latitude != null && value.longitude != null
          ? [value.latitude, value.longitude]
          : null;
      const leafletMap = L.map(element, { zoomControl: false }).setView(
        start ?? DEFAULT_CENTER,
        start ? POINT_ZOOM : DEFAULT_ZOOM,
      );
      L.control
        .zoom({ zoomInTitle: 'Aumenta lo zoom', zoomOutTitle: 'Riduci lo zoom' })
        .addTo(leafletMap);
      leafletMap.attributionControl.setPrefix(false);
      L.tileLayer(tileUrl, { attribution, maxZoom: 19 }).addTo(leafletMap);
      leafletMap.on('click', (event: Leaflet.LeafletMouseEvent) =>
        this.moved(event.latlng.lat, event.latlng.lng),
      );
      this.map = leafletMap;
      const view = this.document.defaultView;
      if (view && 'ResizeObserver' in view) {
        new view.ResizeObserver(() => leafletMap.invalidateSize()).observe(element);
      }
      this.mapStatus.set('ready');
    } catch {
      this.mapStatus.set('error');
    }
  }

  private showPoint(value: LocationValue | null): void {
    const L = this.leaflet;
    const leafletMap = this.map;
    if (!L || !leafletMap) {
      return;
    }
    if (value?.latitude == null || value.longitude == null) {
      this.marker?.remove();
      this.marker = null;
      return;
    }
    const position: [number, number] = [value.latitude, value.longitude];
    if (this.marker) {
      this.marker.setLatLng(position);
      return;
    }
    // Segnaposto in CSS, senza immagini: la goccia nera con il bordo bianco della tavola 2B.
    const icon = L.divIcon({
      className: '',
      html: '<span class="block size-7 -rotate-45 rounded-full rounded-bl-none border-3 border-white bg-ink shadow-md"></span>',
      iconSize: [28, 28],
      iconAnchor: [14, 30],
    });
    const marker = L.marker(position, {
      draggable: true,
      icon,
      title: 'Posizione del cantiere',
      alt: 'Posizione del cantiere',
    }).addTo(leafletMap);
    marker.on('dragend', () => {
      const point = marker.getLatLng();
      this.moved(point.lat, point.lng);
    });
    this.marker = marker;
    leafletMap.setView(position, Math.max(leafletMap.getZoom(), POINT_ZOOM - 2));
  }
}

function round(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}
